import React from 'react';
import './ActivationConfirm.scss';
import { Card } from 'primereact/card';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { ACTIVATE_CONFIRM, ACTIVATE_VALIDATE } from '../../api/urls';
import { FloatLabel } from 'primereact/floatlabel';
import { setActivateTrue } from '../../app/activateSlice';
import { useDispatch } from 'react-redux';


const ActivationConfirm: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();

    const token = React.useMemo(() => {
        const pathSegments = location.pathname.split('/');
        const confirmIndex = pathSegments.findIndex(segment => segment === 'activate-confirm');
        if (confirmIndex !== -1 && confirmIndex + 1 < pathSegments.length) {
            return pathSegments[confirmIndex + 1];
        }
        return null;
    }, [location.pathname]);

    const [newPassword, setNewPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [validating, setValidating] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [success, setSuccess] = React.useState<string | null>(null);

    React.useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setError('Brak tokenu aktywacji konta');
                setValidating(false);
                return;
            }

            try {
                const response = await fetch(ACTIVATE_VALIDATE, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token }),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    if (
                        errorData &&
                        errorData.token &&
                        Array.isArray(errorData.token) &&
                        errorData.token.some((msg: string) => msg.includes("User is already active"))
                    ) {
                        dispatch(setActivateTrue());
                        navigate('/login/', { replace: true });
                        return;
                    }
                    navigate('/activation-expired/', { replace: true });
                    return;
                }

                setValidating(false);
            } catch (err) {
                setError('Wystąpił błąd podczas walidacji tokenu');
                setValidating(false);
            }
        };

        validateToken();
    }, [token, navigate, dispatch]);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (newPassword !== confirmPassword) {
            setError('Nowe hasła muszą się zgadzać.');
            return;
        }

        if (newPassword.length < 8) {
            setError('Nowe hasło musi mieć co najmniej 8 znaków.');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(ACTIVATE_CONFIRM, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token: token,
                    new_password: newPassword
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                // Handle different error formats
                if (errorData.token && Array.isArray(errorData.token)) {
                    throw new Error(errorData.token[0]);
                } else if (typeof errorData === 'string') {
                    throw new Error(errorData);
                } else {
                    throw new Error('Nie udało się aktywować konta');
                }
            }

            setSuccess('Konto zostało aktywowane pomyślnie. Możesz się teraz zalogować.');
            setNewPassword('');
            setConfirmPassword('');

            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate('/login/', { replace: true });
            }, 3000);
        } catch (err: any) {
            setError(err?.message || 'Wystąpił błąd podczas aktywacji konta');
        } finally {
            setLoading(false);
        }
    };

    if (validating) {
        return (
            <>
                <div className='d-flex justify-content-center pt-5'>
                    <img
                        alt='mooveno-logo'
                        className="logo"
                        src={`/login/mooveno-logo-dark.svg`}
                        style={{ width: 200, height: 'auto', maxWidth: '90%', cursor: 'pointer' }}
                        onClick={() => navigate('/')}
                    />
                </div>
                <div className='card-margins d-flex justify-content-center row'>
                    <Card style={{ borderRadius: '14px', maxWidth: 480, width: '100%' }}>
                        <div className="d-flex flex-column align-items-center pb-2">
                            <div className="d-flex justify-content-center">
                                <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                            </div>
                            <h4 className="mt-3">Weryfikowanie...</h4>
                        </div>
                    </Card>
                </div>
            </>
        );
    }

    return (
        <>
            <div className='d-flex justify-content-center pt-5'>
                <img
                    alt='mooveno-logo'
                    className="logo"
                    src={`/login/mooveno-logo-dark.svg`}
                    style={{ width: 200, height: 'auto', maxWidth: '90%', cursor: 'pointer' }}
                    onClick={() => navigate('/')}
                />
            </div>
            <div className='card-margins d-flex justify-content-center row'>
                <Card style={{ borderRadius: '14px', maxWidth: 480, width: '100%' }}>
                    <div className="d-flex flex-column align-items-center mb-5">
                        <h4>Nowe hasło</h4>
                        <span className='px-5'>Wprowadź nowe hasło dla swojego konta.</span>
                    </div>
                    <form onSubmit={onSubmit} className='d-flex column gap-3 justify-content-center'>
                        <div className='password-input'>
                            <div className='pb-4'>
                                <span className="p-float-label">
                                    <FloatLabel>
                                        <Password
                                            id="newPassword"
                                            inputId="newPassword"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            toggleMask
                                            feedback={false}
                                            className='w-100'
                                            inputStyle={{ width: '100%' }}
                                        />
                                        <label htmlFor="newPassword">Nowe hasło</label>
                                    </FloatLabel>
                                </span>
                            </div>
                            <div className='pb-4'>
                                <span className="p-float-label">
                                    <FloatLabel>
                                        <Password
                                            id="confirmPassword"
                                            inputId="confirmPassword"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            toggleMask
                                            feedback={false}
                                            className='w-100'
                                            inputStyle={{ width: '100%' }}
                                        />
                                        <label htmlFor="confirmPassword">Potwierdź nowe hasło</label>
                                    </FloatLabel>
                                </span>
                            </div>
                            <Button
                                className="login-button"
                                type='submit'
                                label={loading ? 'Zmienianie hasła…' : 'Zmień hasło'}
                                loading={loading}
                                disabled={!newPassword || !confirmPassword}
                            />
                            <div style={{ minHeight: '24px', marginTop: '8px', marginBottom: '0' }}>
                                {error && <div className='text-danger' role='alert'>{error}</div>}
                                {success && <div className='text-success' role='status'>{success}</div>}
                            </div>
                            {success && <Button
                                className="go-back-login-button mt-3"
                                label={"Powrót do logowania"}
                                icon="pi pi-arrow-left"
                                iconPos="left"
                                onClick={() => navigate('/login/')}
                                style={{ borderRadius: '10px' }}
                            />}

                        </div>
                    </form>
                </Card>
                <p className='d-flex justify-content-center pt-4'>© 2025 Mooveno. Wszystkie prawa zastrzeżone</p>
            </div>
        </>
    );
};

export default ActivationConfirm; 